import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Video, ArrowUpRight, MapPin, Mail, MessageCircle, Network } from "lucide-react";
import { DOWNLOAD_URL, REXXPONS_URL, LAW_FIRM, NETWORK_APPS } from "../../lib/data";
import { useLanguage } from "../../lib/i18n";

const EASE = [0.16, 1, 0.3, 1];

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer className="relative pt-24 pb-10 border-t border-white/[0.06] overflow-hidden bg-[#04070F]" data-testid="site-footer">
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-blue-600/[0.07] rounded-full blur-[160px]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">

        {/* Top: app identity */}
        <div className="grid md:grid-cols-12 gap-12 pb-16 border-b border-white/[0.06]">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2.5 mb-6">
              <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-blue-500 text-white">
                <Video size={18} strokeWidth={2.5} />
              </span>
              <span className="font-head font-semibold text-lg tracking-tight">Rexx Calling</span>
            </Link>
            <p className="text-slate-400 max-w-sm leading-relaxed">{t("footer.tagline")}</p>
            <a href={DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-8 px-6 py-3 rounded-full bg-white text-[#040814] font-semibold hover:bg-blue-500 hover:text-white transition-colors duration-300" data-testid="footer-download-btn">
              {t("footer.downloadNow")} <ArrowUpRight size={16} />
            </a>
          </div>

          {/* Law firm */}
          <div className="md:col-span-4 md:col-start-9">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">{t("footer.legal")}</span>
            <h4 className="font-head text-xl tracking-tight mt-5 mb-5" data-testid="footer-lawfirm-name">{LAW_FIRM.name}</h4>
            <ul className="space-y-4 text-[15px]">
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin size={17} className="text-blue-400 mt-0.5 flex-shrink-0" />
                <span data-testid="footer-lawfirm-address">{LAW_FIRM.address}</span>
              </li>
              <li>
                <a href={`mailto:${LAW_FIRM.email}`} className="group flex items-start gap-3 text-slate-400 hover:text-white transition-colors duration-300" data-testid="footer-lawfirm-email">
                  <Mail size={17} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <span className="break-all">{LAW_FIRM.email}</span>
                </a>
              </li>
              <li>
                <a href={LAW_FIRM.whatsappLink} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-3 text-slate-400 hover:text-white transition-colors duration-300" data-testid="footer-lawfirm-whatsapp">
                  <MessageCircle size={17} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <span>{LAW_FIRM.whatsapp}</span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Rexxpons Network brand + apps */}
        <div className="grid md:grid-cols-12 gap-12 py-16 border-b border-white/[0.06]">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5 mb-5">
              <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-white/[0.04] border border-white/10 text-blue-400">
                <Network size={17} strokeWidth={2} />
              </span>
              <span className="font-head font-semibold text-lg tracking-tight">{t("footer.network")}</span>
            </div>
            <p className="text-slate-400 max-w-sm leading-relaxed">{t("footer.networkDesc")}</p>
            <a href={REXXPONS_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 mt-6 text-blue-400 hover:text-blue-300 font-medium transition-colors duration-300" data-testid="footer-rexxpons-link">
              {t("footer.visitNetwork")}
              <ArrowUpRight size={15} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">{t("footer.apps")}</span>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3.5">
              {NETWORK_APPS.map((app) => (
                <li key={app.href}>
                  <a href={app.href} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors duration-300" data-testid={`footer-app-${app.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    <span className="w-1 h-1 rounded-full bg-blue-500/60 group-hover:bg-blue-400 transition-colors duration-300" />
                    {app.label}
                    <ArrowUpRight size={13} className="text-slate-600 group-hover:text-blue-400 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Giant wordmark */}
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="font-head font-thin text-[15vw] leading-none tracking-tighter text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.06)] select-none pointer-events-none whitespace-nowrap overflow-hidden py-4">
          Rexxpons
        </motion.div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 border-t border-white/[0.06]">
          <p className="text-sm text-slate-500" data-testid="footer-copyright">© 2026 {t("footer.copyright")}</p>
          <p className="text-sm text-slate-600">{t("footer.rights")}</p>
        </div>
      </div>
    </footer>
  );
};
